import React, { useState, useEffect } from 'react';
import { API } from '../../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { RefreshCw, Clock, Database, Zap, AlertTriangle } from 'lucide-react';

/**
 * Operator Debug Page - System status and diagnostics
 */
export const OperatorDebug = () => {
  const [loading, setLoading] = useState(true);
  const [reminderSchedule, setReminderSchedule] = useState(null);
  const [enrichmentLibrary, setEnrichmentLibrary] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reminderRes, enrichmentRes, pipelineRes] = await Promise.all([
        fetch(`${API}/debug/reminder-schedule`).then(r => r.json()).catch(() => null),
        fetch(`${API}/debug/enrichment-library`).then(r => r.json()).catch(() => null),
        fetch(`${API}/debug/pipeline-status`).then(r => r.json()).catch(() => null),
      ]);
      
      setReminderSchedule(reminderRes);
      setEnrichmentLibrary(enrichmentRes);
      setPipelineStatus(pipelineRes);
    } catch (err) {
      console.error('Debug data error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">System Status</h1>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Reminder Schedule */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              Reminder Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reminderSchedule ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Current Time (IST)</span>
                  <span className="font-mono">{reminderSchedule.current_time_ist}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Scheduler Runs</span>
                  <span className="font-mono text-xs">{reminderSchedule.scheduler_runs_at}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Default Time</span>
                  <span className="font-mono">{reminderSchedule.default_reminder_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Next Slot</span>
                  <span className="font-mono text-green-600">{reminderSchedule.next_reminder_slot || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Families</span>
                  <span className="font-medium">{reminderSchedule.total_families}</span>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-2">Time Distribution:</p>
                  <div className="space-y-1">
                    {Object.entries(reminderSchedule.time_distribution || {}).map(([time, count]) => (
                      <div key={time} className="flex justify-between text-xs">
                        <span className="font-mono">{time}</span>
                        <span className="bg-slate-100 px-2 rounded">{count} families</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Loading...</p>
            )}
          </CardContent>
        </Card>

        {/* Enrichment Library */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Enrichment Library
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrichmentLibrary ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Version</span>
                  <span className="font-medium">{enrichmentLibrary.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Total Items</span>
                  <span className="font-medium text-green-600">{enrichmentLibrary.total_items}</span>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-2">By Type:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(enrichmentLibrary.by_type || {}).map(([type, count]) => (
                      <span key={type} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {type}: {count}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-2">By Category:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(enrichmentLibrary.by_category || {}).map(([cat, count]) => (
                      <span key={cat} className="bg-green-50 text-green-700 px-2 py-1 rounded text-xs">
                        {cat}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Loading...</p>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Status */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="w-5 h-5 text-green-500" />
              Pipeline Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pipelineStatus ? (
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800">{pipelineStatus.emails_ingested || 0}</p>
                  <p className="text-xs text-slate-500">Emails Ingested</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800">{pipelineStatus.tasks_extracted || 0}</p>
                  <p className="text-xs text-slate-500">Tasks Extracted</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800">{pipelineStatus.packs_generated || 0}</p>
                  <p className="text-xs text-slate-500">Packs Generated</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{pipelineStatus.whatsapp_sent || 0}</p>
                  <p className="text-xs text-slate-500">WhatsApp Sent</p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Loading...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperatorDebug;
